import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { getCategoryTreeLazy, getCategoryChildren, deleteCategory } from '../../services/category';
import CategoryModal from './CategoryModal';
import CategoryTree from './CategoryTree';

const Categories = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loadedChildren, setLoadedChildren] = useState(new Map());
  const [loadingNodes, setLoadingNodes] = useState(new Set());

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    filterCategories();
  }, [categories, searchTerm, filterStatus]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') {
        params.is_active = filterStatus === 'active';
      }
      
      const response = await getCategoryTreeLazy(params);
      if (response.success) {
        setCategories(response.data);
        setLoadedChildren(new Map());
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      showSnackbar('Failed to load categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryChildren = async (categorySid) => {
    if (loadedChildren.has(categorySid)) {
      return;
    }

    try {
      setLoadingNodes(prev => new Set(prev).add(categorySid));
      
      const params = {};
      if (filterStatus !== 'all') {
        params.is_active = filterStatus === 'active';
      }
      
      const response = await getCategoryChildren(categorySid, params);
      if (response.success) {
        setLoadedChildren(prev => new Map(prev).set(categorySid, response.data));
      }
    } catch (error) {
      console.error('Error loading children:', error);
      showSnackbar('Failed to load subcategories', 'error');
    } finally {
      setLoadingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(categorySid);
        return newSet;
      });
    }
  };

  const filterCategories = () => {
    let filtered = [...categories];

    // Filter by search term
    if (searchTerm) {
      filtered = filterCategoriesBySearch(filtered, searchTerm.toLowerCase());
    }

    setFilteredCategories(filtered);
  };

  const filterCategoriesBySearch = (cats, term) => {
    return cats.map(cat => ({ ...cat })).filter(cat => {
      const matchesSearch = cat.name.toLowerCase().includes(term) || 
                           (cat.description && cat.description.toLowerCase().includes(term));
      
      if (matchesSearch) return true;
      
      // Check loaded children
      const children = loadedChildren.get(cat.sid || cat.id) || [];
      if (children.length > 0) {
        const filteredChildren = filterCategoriesBySearch(children, term);
        if (filteredChildren.length > 0) {
          return true;
        }
      }
      
      return false;
    });
  };

  const handleAddRoot = () => {
    setSelectedCategory(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleAddChild = (category) => {
    setSelectedCategory(category);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleView = (category) => {
    setSelectedCategory(category);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"? This will deactivate the category and all its children.`)) {
      return;
    }

    try {
      await deleteCategory(category.sid || category.id, false); // Soft delete
      showSnackbar('Category deleted successfully', 'success');
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      showSnackbar('Failed to delete category', 'error');
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  const handleModalSuccess = () => {
    setModalOpen(false);
    setSelectedCategory(null);
    loadCategories();
    showSnackbar(
      modalMode === 'create' ? 'Category created successfully' : 'Category updated successfully',
      'success'
    );
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Category Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadCategories}
            sx={{ minWidth: 'auto' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRoot}
            sx={{ minWidth: 'auto' }}
          >
            Add Root Category
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <TextField
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ 
                flex: 1, 
                minWidth: { xs: '100%', sm: 300 },
                maxWidth: { xs: '100%', sm: 400 }
              }}
            />
            <FormControl sx={{ 
              minWidth: { xs: '100%', sm: 200 },
              maxWidth: { xs: '100%', sm: 250 }
            }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Filter by Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Category Tree */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : filteredCategories.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                No categories found. Click "Add Root Category" to create your first category.
              </Alert>
            </Box>
          ) : (
            <Box sx={{ 
              width: '100%',
              overflowX: 'auto',
              minHeight: 200
            }}>
              <CategoryTree
                categories={filteredCategories}
                loadedChildren={loadedChildren}
                loadingNodes={loadingNodes}
                onLoadChildren={loadCategoryChildren}
                onAddChild={handleAddChild}
                onEdit={handleEdit}
                onView={handleView}
                onDelete={handleDelete}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Category Modal */}
      <CategoryModal
        open={modalOpen}
        mode={modalMode}
        category={selectedCategory}
        parentCategory={modalMode === 'create' ? selectedCategory : null}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Categories;

